import type { MenuProps } from "antd/lib/menu";
import { MenuItemType } from "antd/lib/menu/interface";
import Tag from "antd/lib/tag";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@sol/uikit/data-display/Tooltip/Tooltip";

export type MenuItem = Required<MenuProps>["items"][number];

const TaggedLabel = ({ label, tag }: { label: MenuItemType["label"]; tag: string }) => (
    <span className="flex w-full items-center justify-between">
        {label}
        <Tag bordered={false} className="rounded-ant-sm! bg-(--ant-menu-item-selected-color) text-white!">
            {tag}
        </Tag>
    </span>
);

type GetLinkProps = Omit<MenuItemType, "key"> & {
    href: string;
    "data-cy"?: string;
    isNew?: boolean;
    tooltipTitle?: string;
};

const MenuItemLabel = ({ label, href, isNew, "data-cy": dataCy, tooltipTitle }: GetLinkProps) => {
    const [t] = useTranslation();
    const linkRef = useRef<HTMLAnchorElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    useEffect(() => {
        const link = linkRef.current;
        const truncatedLabel = link?.querySelector("[data-truncated-label]");
        if (!truncatedLabel) {
            return undefined;
        }

        const updateTruncation = () => setIsTruncated(truncatedLabel.scrollWidth > truncatedLabel.clientWidth);
        updateTruncation();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateTruncation);
        resizeObserver.observe(truncatedLabel);

        return () => resizeObserver.disconnect();
    }, [label]);

    return (
        <Link href={href} passHref>
            {/* tabIndex prevent the link to interfere with menu focus trap */}
            <a
                ref={linkRef}
                tabIndex={-1}
                data-cy={dataCy}
                className="flex w-full min-w-0"
                onMouseEnter={() => setIsTooltipOpen(true)}
                onMouseLeave={() => setIsTooltipOpen(false)}
            >
                <Tooltip title={tooltipTitle} open={Boolean(tooltipTitle && isTruncated && isTooltipOpen)}>
                    {isNew ? <TaggedLabel label={label} tag={t("tag.new")} /> : label}
                </Tooltip>
            </a>
        </Link>
    );
};

/**
 * Creates a route-aware menu item with a Next.js Link.
 * Shared utility used by all menu widgets.
 */
export const getLink = ({
    href,
    isNew,
    "data-cy": dataCy,
    label,
    tooltipTitle,
    ...item
}: GetLinkProps): MenuItemType => ({
    key: href,
    label: (
        <MenuItemLabel href={href} isNew={isNew} data-cy={dataCy} label={label} tooltipTitle={tooltipTitle} {...item} />
    ),
    ...item,
});
